import React, { useState, useEffect } from 'react';
import { useStore } from '../context/StoreContext';
import { useNavigate } from 'react-router-dom';
import { UserRole } from '../types';
import { Button } from '../components/Button';
import { api } from '../services/api';
import { Store, Lock, Mail, ArrowRight, ShieldAlert, Eye, EyeOff, User as UserIcon, Key, CreditCard, Users, CheckCircle, ExternalLink } from 'lucide-react';

export const Auth = () => {
  const { login, logout, register, loginAsWaiter } = useStore();
  const navigate = useNavigate();
  const [isLoginMode, setIsLoginMode] = useState(true);
  const [isWaiterMode, setIsWaiterMode] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [isRecoveryMode, setIsRecoveryMode] = useState(false);
  const [recoveryEmail, setRecoveryEmail] = useState('');
  const [devResetUrl, setDevResetUrl] = useState('');
  const [reloginNotice, setReloginNotice] = useState(false);

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [name, setName] = useState('');
  const [companyName, setCompanyName] = useState('');
  const [inviteCode, setInviteCode] = useState('');
  const [companyEmail, setCompanyEmail] = useState('');
  const [waiterName, setWaiterName] = useState('');
  const [accessCode, setAccessCode] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [rememberMe, setRememberMe] = useState(true);
  const [loginAudience, setLoginAudience] = useState<'CHOICE' | 'OWNER' | 'OWNER_CONFIRM' | 'EMPLOYEE'>('OWNER');
  const [verifiedOwnerEmail, setVerifiedOwnerEmail] = useState('');

  useEffect(() => {
    const rememberedEmail = localStorage.getItem('ft_remembered_email');
    if (rememberedEmail) {
      setEmail(rememberedEmail);
      setRememberMe(true);
    }
    if (localStorage.getItem('ft_relogin_notice')) {
      setReloginNotice(true);
      localStorage.removeItem('ft_relogin_notice');
    }
  }, []);

  const handleWaiterSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setReloginNotice(false);
    setLoading(true);
    try {
      const result = await loginAsWaiter(companyEmail, waiterName, accessCode);
      if (result?.success) {
        navigate('/waiter');
      } else {
        setError(result?.message || 'Erro ao tentar entrar como garçom.');
      }
    } catch (err) {
      setError('Não foi possível entrar agora. Tente novamente em instantes.');
    } finally {
      setLoading(false);
    }
  };

  const handlePasswordResetRequest = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSuccessMessage('');
    setDevResetUrl('');
    setLoading(true);
    try {
      const result = await api.auth.requestPasswordReset(recoveryEmail || email);
      if (result?.success) {
        setSuccessMessage(result?.message || 'Se esse e-mail existir, enviaremos um link de recuperação.');
        if (result.devResetUrl) setDevResetUrl(result.devResetUrl);
      } else {
        setError(result?.message || 'Não foi possível solicitar a recuperação agora.');
      }
    } catch (err) {
      setError('Erro inesperado ao solicitar recuperação.');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      if (isLoginMode) {
        const loginEmail = loginAudience === 'OWNER_CONFIRM' ? verifiedOwnerEmail : email;
        const result = await login(loginEmail, password, rememberMe);
        if (result?.success) {
          if (loginAudience === 'OWNER' && result.role !== UserRole.OWNER && result.role !== UserRole.SAAS_ADMIN) {
            logout();
            setError('A primeira entrada deve ser feita com a conta do dono da empresa.');
            return;
          }
          if (loginAudience === 'EMPLOYEE' && result.role !== UserRole.CASHIER) {
            logout();
            setError('Este acesso é de dono ou gerência. Escolha a opção correspondente.');
            return;
          }
          if (rememberMe) {
            localStorage.setItem('ft_remembered_email', loginEmail);
          } else {
            localStorage.removeItem('ft_remembered_email');
          }
          if (loginAudience === 'OWNER' && result.role === UserRole.OWNER && result.subLoginEnabled) {
            sessionStorage.removeItem('ft_subaccess_active');
            navigate('/access');
            return;
          }
          navigate(result.role === UserRole.SAAS_ADMIN ? '/admin' : '/pos');
        } else {
          setError(result?.message || 'Erro na autenticação');
        }
      } else {
        const result = await register(companyName, name, email, password, inviteCode.trim().toUpperCase());
        if (result?.success) {
          alert('Conta criada com sucesso! Use suas credenciais para entrar.');
          setIsLoginMode(true);
        } else setError(result?.message || 'Erro no cadastro');
      }
    } catch (err) {
      setError('Não foi possível concluir agora. Tente novamente em instantes.');
    } finally {
      setLoading(false);
    }
  };

  const inputClass = 'w-full pl-12 pr-4 py-4 bg-slate-50 border border-slate-200 rounded-2xl focus:ring-4 focus:ring-slate-100 focus:border-slate-400 outline-none font-bold text-sm transition-all';
  const labelClass = 'text-[10px] font-black text-slate-400 uppercase tracking-widest ml-1';

  const renderAlert = (message: string, tone: 'error' | 'warning' = 'error') => (
    <div className={`mb-5 p-4 ${tone === 'error' ? 'bg-red-50 border-red-100 text-red-600' : 'bg-amber-50 border-amber-200 text-amber-700'} border text-xs font-bold rounded-2xl flex items-center gap-3`}>
      <ShieldAlert className="w-5 h-5 flex-shrink-0"/> {message}
    </div>
  );

  const renderSuccess = (message: string) => (
    <div className="mb-5 p-4 bg-emerald-50 border border-emerald-100 text-emerald-700 text-xs font-bold rounded-2xl flex items-center gap-3">
      <CheckCircle className="w-5 h-5 flex-shrink-0"/> {message}
    </div>
  );

  const renderWaiterForm = () => (
    <div className="w-full max-w-md mx-auto">
      <div className="mb-8">
        <p className="text-[11px] font-black uppercase tracking-[0.25em] text-emerald-600 mb-3">Entrada da equipe</p>
        <h2 className="text-3xl font-black text-slate-950 tracking-tight">Acesso do garçom</h2>
        <p className="text-slate-500 text-sm mt-2 font-medium">Use os dados liberados pelo gerente para abrir comandas pelo celular.</p>
      </div>
      {error && renderAlert(error)}
      <form onSubmit={handleWaiterSubmit} className="space-y-4">
        <div className="space-y-1"><label className={labelClass}>Email da empresa</label><div className="relative"><Mail className="absolute left-4 top-4 w-5 h-5 text-slate-300" /><input required type="email" placeholder="Email do dono do estabelecimento" className={inputClass} value={companyEmail} onChange={(e) => setCompanyEmail(e.target.value)} /></div></div>
        <div className="space-y-1"><label className={labelClass}>Seu nome</label><div className="relative"><UserIcon className="absolute left-4 top-4 w-5 h-5 text-slate-300" /><input required type="text" placeholder="Como você se chama" className={inputClass} value={waiterName} onChange={(e) => setWaiterName(e.target.value)} /></div></div>
        <div className="space-y-1"><label className={labelClass}>Código da empresa</label><div className="relative"><Lock className="absolute left-4 top-4 w-5 h-5 text-slate-300" /><input required type="text" placeholder="Código de 4-6 dígitos" className={inputClass} value={accessCode} onChange={(e) => setAccessCode(e.target.value)} /></div></div>
        <Button type="submit" className="w-full !mt-6 h-16 rounded-2xl text-xs font-black uppercase tracking-[0.2em]" disabled={loading}>{loading ? 'Validando...' : <span className="flex items-center gap-3">Entrar <ArrowRight className="w-5 h-5" /></span>}</Button>
      </form>
      <button onClick={() => setIsWaiterMode(false)} className="w-full mt-6 text-sm font-black text-slate-400 hover:text-slate-900 transition-colors">Voltar ao login principal</button>
    </div>
  );

  const renderMainForm = () => {
    if (isRecoveryMode) {
      return (
        <div className="w-full max-w-md mx-auto">
          <div className="mb-8">
            <p className="text-[11px] font-black uppercase tracking-[0.25em] text-emerald-600 mb-3">Recuperação segura</p>
            <h2 className="text-3xl font-black text-slate-950 tracking-tight">Recupere sua senha</h2>
            <p className="text-slate-500 text-sm mt-2 font-medium">Informe o e-mail do dono/operador. Se a conta existir, enviaremos um link para criar uma nova senha.</p>
            <p className="mt-3 rounded-2xl border border-amber-100 bg-amber-50 px-4 py-3 text-xs font-bold text-amber-700">Sua senha atual continua valendo até você abrir o link e confirmar uma nova senha.</p>
          </div>
          {error && renderAlert(error)}
          {successMessage && renderSuccess(successMessage)}
          {devResetUrl && (
            <a href={devResetUrl} className="mb-5 flex items-center justify-between gap-3 rounded-2xl border border-emerald-100 bg-emerald-50 p-4 text-xs font-black text-emerald-700 hover:bg-emerald-100 transition-colors">
              Abrir link local de teste <ExternalLink className="w-4 h-4" />
            </a>
          )}
          <form onSubmit={handlePasswordResetRequest} className="space-y-4">
            <div className="space-y-1">
              <label className={labelClass}>Email da conta</label>
              <div className="relative">
                <Mail className="absolute left-4 top-4 w-5 h-5 text-slate-300" />
                <input required type="email" className={inputClass} value={recoveryEmail} onChange={(e) => setRecoveryEmail(e.target.value)} placeholder="email@empresa.com" />
              </div>
            </div>
            <Button type="submit" className="w-full !mt-6 h-16 rounded-2xl text-xs font-black uppercase tracking-[0.2em] shadow-xl shadow-slate-900/10" disabled={loading}>
              {loading ? 'Enviando...' : <span className="flex items-center gap-3">Enviar link seguro <ArrowRight className="w-5 h-5" /></span>}
            </Button>
          </form>
          <button onClick={() => { setIsRecoveryMode(false); setError(''); setSuccessMessage(''); setDevResetUrl(''); }} className="w-full mt-6 text-sm font-black text-slate-400 hover:text-slate-900 transition-colors">Voltar ao login</button>
        </div>
      );
    }

    if (isLoginMode && loginAudience === 'CHOICE') {
      return (
        <div className="w-full max-w-md mx-auto">
          <div className="mb-8">
            <p className="text-[11px] font-black uppercase tracking-[0.25em] text-emerald-600 mb-3">Conta do dono validada</p>
            <h2 className="text-3xl font-black text-slate-950 tracking-tight">Quem vai acessar?</h2>
            <p className="text-slate-500 text-sm mt-2 font-medium">Escolha a sessão que será usada agora. Cada pessoa confirma as próprias credenciais.</p>
          </div>

          <div className="space-y-4">
            <button type="button" onClick={() => { setEmail(verifiedOwnerEmail); setPassword(''); setLoginAudience('OWNER_CONFIRM'); }} className="group w-full rounded-3xl border border-slate-200 bg-slate-50 p-5 text-left transition-all hover:-translate-y-0.5 hover:border-slate-400 hover:bg-white hover:shadow-lg focus:outline-none focus:ring-4 focus:ring-slate-100">
              <span className="mb-4 flex h-12 w-12 items-center justify-center rounded-2xl bg-slate-950 text-white"><ShieldAlert className="h-6 w-6" /></span>
              <span className="block text-lg font-black text-slate-950">Entrar como dono</span>
              <span className="mt-1 block text-sm font-medium leading-6 text-slate-500">Confirme a senha do dono para usar as áreas administrativas da empresa.</span>
              <span className="mt-4 inline-flex items-center gap-2 text-xs font-black uppercase tracking-widest text-slate-900">Continuar <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" /></span>
            </button>

            <button type="button" onClick={() => { setEmail(''); setPassword(''); setLoginAudience('EMPLOYEE'); }} className="group w-full rounded-3xl border border-emerald-100 bg-emerald-50/70 p-5 text-left transition-all hover:-translate-y-0.5 hover:border-emerald-300 hover:bg-white hover:shadow-lg focus:outline-none focus:ring-4 focus:ring-emerald-50">
              <span className="mb-4 flex h-12 w-12 items-center justify-center rounded-2xl bg-emerald-600 text-white"><Users className="h-6 w-6" /></span>
              <span className="block text-lg font-black text-slate-950">Sou funcionário</span>
              <span className="mt-1 block text-sm font-medium leading-6 text-slate-500">Entre com o e-mail e a senha que o dono cadastrou para você.</span>
              <span className="mt-4 inline-flex items-center gap-2 text-xs font-black uppercase tracking-widest text-emerald-700">Continuar <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" /></span>
            </button>
          </div>

          <button type="button" onClick={() => { logout(); setLoginAudience('OWNER'); setVerifiedOwnerEmail(''); setPassword(''); }} className="mt-7 w-full text-sm font-black text-slate-400 transition-colors hover:text-slate-900">Usar outra conta do dono</button>
        </div>
      );
    }

    return (
    <div className="w-full max-w-md mx-auto">
      <div className="mb-8">
        <p className="text-[11px] font-black uppercase tracking-[0.25em] text-emerald-600 mb-3">Painel seguro FlowTab</p>
        <h2 className="text-3xl font-black text-slate-950 tracking-tight">{isLoginMode ? (loginAudience === 'EMPLOYEE' ? 'Entrada do funcionário' : loginAudience === 'OWNER_CONFIRM' ? 'Confirmar acesso do dono' : 'Entrada do dono') : 'Ative seu estabelecimento'}</h2>
        <p className="text-slate-500 text-sm mt-2 font-medium">{isLoginMode ? (loginAudience === 'EMPLOYEE' ? 'Use somente as credenciais liberadas pelo dono da empresa.' : loginAudience === 'OWNER_CONFIRM' ? 'Informe novamente sua senha para abrir a sessão de dono.' : 'Entre primeiro com a conta do dono para escolher quem usará o sistema agora.') : 'Crie o acesso para começar a operar com o FlowTab.'}</p>
      </div>
      {error && renderAlert(error)}
      {reloginNotice && renderAlert('Atualizamos o FlowTab. Para proteger seus dados, faça login novamente.', 'warning')}
      <form onSubmit={handleSubmit} className="space-y-4">
        {!isLoginMode && (
          <>
            <div className="space-y-1"><label className={labelClass}>Nome da empresa</label><div className="relative"><Store className="absolute left-4 top-4 w-5 h-5 text-slate-300" /><input required type="text" className={inputClass} value={companyName} onChange={(e) => setCompanyName(e.target.value)} /></div></div>
            <div className="space-y-1"><label className={labelClass}>Seu nome</label><div className="relative"><UserIcon className="absolute left-4 top-4 w-5 h-5 text-slate-300" /><input required type="text" className={inputClass} value={name} onChange={(e) => setName(e.target.value)} /></div></div>
            <div className="space-y-1"><label className="text-[10px] font-black text-emerald-500 uppercase tracking-widest ml-1">Código de convite</label><div className="relative"><Key className="absolute left-4 top-4 w-5 h-5 text-emerald-300" /><input required type="text" placeholder="Código fornecido pela equipe FlowTab" className="w-full pl-12 pr-4 py-4 bg-emerald-50 border border-emerald-100 rounded-2xl focus:ring-4 focus:ring-emerald-50 outline-none font-black text-emerald-700 placeholder:text-emerald-300 text-sm" value={inviteCode} onChange={(e) => setInviteCode(e.target.value.toUpperCase())} /></div></div>
          </>
        )}
        {loginAudience === 'OWNER_CONFIRM' ? (
          <div className="rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3"><p className={labelClass}>Conta validada</p><p className="mt-1 text-sm font-black text-slate-700">{verifiedOwnerEmail}</p></div>
        ) : <div className="space-y-1"><label className={labelClass}>Email</label><div className="relative"><Mail className="absolute left-4 top-4 w-5 h-5 text-slate-300" /><input required type="email" className={inputClass} value={email} onChange={(e) => setEmail(e.target.value)} /></div></div>}
        <div className="space-y-1"><label className={labelClass}>Senha</label><div className="relative"><Lock className="absolute left-4 top-4 w-5 h-5 text-slate-300" /><input required type={showPassword ? 'text' : 'password'} className="w-full pl-12 pr-14 py-4 bg-slate-50 border border-slate-200 rounded-2xl focus:ring-4 focus:ring-slate-100 focus:border-slate-400 outline-none font-bold text-sm transition-all" value={password} onChange={(e) => setPassword(e.target.value)} /><button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-4 top-4 text-slate-300 hover:text-slate-700">{showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}</button></div></div>
        {isLoginMode && loginAudience === 'OWNER' && <button type="button" onClick={() => { setRecoveryEmail(email); setIsRecoveryMode(true); setError(''); setSuccessMessage(''); }} className="text-xs font-black text-slate-400 hover:text-emerald-600 transition-colors">Esqueci minha senha</button>}
        {isLoginMode && (<label className="flex items-center gap-2 text-sm text-slate-600 font-medium cursor-pointer pt-1"><input type="checkbox" className="w-4 h-4 text-slate-900 bg-slate-100 border-slate-300 rounded focus:ring-slate-500" checked={rememberMe} onChange={(e) => setRememberMe(e.target.checked)} />Lembrar-me neste dispositivo</label>)}
        <Button type="submit" className="w-full !mt-6 h-16 rounded-2xl text-xs font-black uppercase tracking-[0.2em] shadow-xl shadow-slate-900/10" disabled={loading}>{loading ? 'Sincronizando...' : <span className="flex items-center gap-3">{isLoginMode ? (loginAudience === 'OWNER' ? 'Fazer login' : loginAudience === 'OWNER_CONFIRM' ? 'Entrar como dono' : 'Entrar como funcionário') : 'Ativar sistema'} <ArrowRight className="w-5 h-5" /></span>}</Button>
      </form>
      <div className="mt-8 text-center space-y-4">
        {isLoginMode && loginAudience === 'OWNER' ? (
          <>
            <button onClick={() => setIsLoginMode(false)} className="block w-full text-sm font-black text-slate-400 hover:text-slate-900 transition-colors">Cadastrar um novo estabelecimento</button>
            <div className="relative"><div className="absolute inset-0 flex items-center"><span className="w-full border-t border-slate-200"></span></div><div className="relative flex justify-center text-xs uppercase"><span className="bg-white px-3 text-slate-400 font-black tracking-widest">equipe</span></div></div>
            <Button onClick={() => setIsWaiterMode(true)} variant="secondary" className="w-full h-12 rounded-2xl"><Users className="w-4 h-4"/> Entrar como garçom</Button>
          </>
        ) : isLoginMode ? <button type="button" onClick={() => { logout(); setLoginAudience('CHOICE'); setPassword(''); }} className="text-sm font-black text-slate-400 hover:text-slate-900 transition-colors">Voltar para escolher acesso</button> : <button onClick={() => { setIsLoginMode(true); setLoginAudience('OWNER'); }} className="text-sm font-black text-slate-400 hover:text-slate-900 transition-colors">Já possui cadastro Fazer login</button>}

        {isLoginMode && loginAudience === 'OWNER' && (
          <a
            href="/demo-flowtab.html"
            className="flex h-12 w-full items-center justify-center gap-2 rounded-2xl border border-emerald-100 bg-emerald-50 text-sm font-black text-emerald-700 shadow-sm transition-all hover:-translate-y-0.5 hover:bg-emerald-100"
          >
            Ver demonstração do FlowTab <ExternalLink className="h-4 w-4" />
          </a>
        )}
      </div>
    </div>
  );
  };

  return (
    <div className="min-h-screen bg-[#050915] text-slate-950 font-sans relative overflow-hidden">
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_15%_20%,rgba(16,185,129,.18),transparent_30%),radial-gradient(circle_at_85%_10%,rgba(59,130,246,.22),transparent_34%),linear-gradient(135deg,#050915_0%,#0f172a_48%,#052e2b_100%)]"></div>
      <div className="absolute inset-0 opacity-[0.05] bg-[linear-gradient(90deg,#fff_1px,transparent_1px),linear-gradient(#fff_1px,transparent_1px)] bg-[length:44px_44px]"></div>

      <main className="relative z-10 min-h-screen flex flex-col px-4 py-5 md:px-8 md:py-7">
        <header className="max-w-7xl mx-auto w-full flex items-center justify-between mb-6">
          <img src="/flowtab-logo-clean-transparent.png" alt="FlowTab" className="w-40 md:w-48 object-contain" />
          <div className="hidden md:flex items-center gap-3 text-xs font-black uppercase tracking-[0.2em] text-slate-300">
            <span>PDV</span><span className="text-emerald-300">&bull;</span><span>Estoque</span><span className="text-emerald-300">&bull;</span><span>Delivery</span>
          </div>
        </header>

        <section className="max-w-7xl mx-auto w-full flex-1 grid lg:grid-cols-[1.15fr_.85fr] gap-6 items-stretch">
          <div className="hidden lg:flex relative rounded-[2.5rem] border border-white/10 bg-white/[0.06] shadow-2xl shadow-black/30 overflow-hidden p-8 xl:p-10">
            <div className="absolute -right-20 -top-20 w-80 h-80 bg-emerald-400/20 rounded-full blur-3xl"></div>
            <div className="absolute left-10 bottom-10 w-72 h-72 bg-blue-500/20 rounded-full blur-3xl"></div>
            <div className="relative z-10 w-full flex flex-col justify-between">
              <div>
                <p className="text-[11px] font-black uppercase tracking-[0.32em] text-emerald-300 mb-5">Sistema de comandas para operações reais</p>
                <h1 className="text-5xl xl:text-6xl font-black text-white leading-[1.02] max-w-3xl">Um painel bonito o bastante para vender. Rápido o bastante para usar no salão.</h1>
                <p className="text-slate-300 text-lg mt-6 max-w-2xl leading-relaxed">O FlowTab junta balcão, mesas, garçons, estoque, nota fiscal, delivery e fechamento de caixa em uma experiência simples para bares e restaurantes.</p>
              </div>

              <div className="relative mt-10">
                <div className="rounded-[2rem] bg-slate-950/90 border border-white/10 p-4 shadow-2xl">
                  <div className="flex items-center justify-between mb-4">
                    <div>
                      <p className="text-slate-400 text-xs font-bold">Hoje no caixa</p>
                      <p className="text-white text-3xl font-black">R$ 8.642,50</p>
                    </div>
                    <span className="px-3 py-1.5 rounded-full bg-emerald-400/15 text-emerald-200 border border-emerald-300/20 text-xs font-black">ONLINE</span>
                  </div>
                  <div className="grid grid-cols-4 gap-3">
                    {[1,2,3,4,5,6,7,8].map((table) => (
                      <div key={table} className={`rounded-2xl p-4 border ${table < 4 ? 'bg-blue-500/15 border-blue-300/20' : 'bg-white/5 border-white/10'}`}>
                        <p className="text-white text-2xl font-black">{table}</p>
                        <p className="text-[10px] text-slate-400 font-black uppercase mt-1">{table < 4 ? 'Ocupada' : 'Livre'}</p>
                      </div>
                    ))}
                  </div>
                </div>
                <div className="absolute -right-5 -bottom-8 w-64 rounded-[2rem] bg-white shadow-2xl border border-slate-200 p-5">
                  <div className="flex items-center justify-between mb-4"><p className="font-black text-slate-900">Pedido novo</p><span className="text-[10px] font-black text-emerald-600 bg-emerald-50 px-2 py-1 rounded-full">COZINHA</span></div>
                  <div className="space-y-3">
                    <div className="flex justify-between text-sm"><span>2x Brahma 600</span><b>R$ 30</b></div>
                    <div className="flex justify-between text-sm"><span>1x Porção</span><b>R$ 34</b></div>
                    <div className="h-2 rounded-full bg-slate-100 overflow-hidden"><div className="h-full w-2/3 bg-emerald-500 rounded-full"></div></div>
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-3 gap-4 mt-16">
                <div className="rounded-3xl bg-white/10 border border-white/10 p-5"><CreditCard className="w-6 h-6 text-emerald-300 mb-3"/><p className="text-white font-black">PDV veloz</p><p className="text-slate-400 text-sm mt-1">Mesa, balcão e pagamento.</p></div>
                <div className="rounded-3xl bg-white/10 border border-white/10 p-5"><Store className="w-6 h-6 text-blue-200 mb-3"/><p className="text-white font-black">Estoque vivo</p><p className="text-slate-400 text-sm mt-1">Baixa automática e XML.</p></div>
                <div className="rounded-3xl bg-white/10 border border-white/10 p-5"><Users className="w-6 h-6 text-emerald-300 mb-3"/><p className="text-white font-black">Equipe junto</p><p className="text-slate-400 text-sm mt-1">Garçom e caixa no mesmo ritmo.</p></div>
              </div>
            </div>
          </div>

          <div className="rounded-[2.5rem] bg-white shadow-2xl shadow-black/30 border border-white/70 p-6 md:p-10 flex flex-col justify-between min-h-[680px]">
            {isWaiterMode ? renderWaiterForm() : renderMainForm()}
            <footer className="pt-8 mt-8 border-t border-slate-100 text-center">
              <p className="text-[11px] font-black uppercase tracking-[0.24em] text-slate-400">FlowTab para bares, restaurantes e lanchonetes</p>
              <p className="text-xs text-slate-400 mt-2">Controle de comandas  estoque  delivery  relatórios  impressão térmica</p>
            </footer>
          </div>
        </section>
      </main>
    </div>
  );
};
